import * as classnames from 'classnames'
import * as React from 'react'
import * as screenfull from 'screenfull'
import get from 'lodash-es/get'
import { BaseVideoPlayer, PlayState } from '@envato/react-video'

import EnhancedLazyLoad from '../../../components/enhancedLazyLoad'
import LoadingSpinner from '../../icons/loadingSpinner'
import ClickOverlay from '../../video/clickOverlay'
import VideoControlsFullscreen from '../../video/videoControlsFullscreen'
import { IItemCard } from '../interface'
import LockupPreviewActionPanel from '../lockupPreviewActionPanel'

import * as styles from './lockupVideoPreview.module.css'

const firstVisibleVideosCount = 6

const { PLAY, PAUSE, RESET } = PlayState

interface IState {
  playState: PlayState
  wasTouchMoveDetected: boolean
  hideCursor: boolean
  videoPlayPromise: any
  videoLooped: boolean
  reportedEvents: string[]
  videoProgress: number
  dragging: boolean
  seek?: number
  duration?: number
}

interface ILockupVideoPreview extends IItemCard {
  reportVideoProgress?: (eventName: string, videoSrc: string) => void
  reportVideoFullscreen?: (itemId: number) => void
  setUserVideoAutoPlayStatus?: (status: boolean) => void
  unmuteVideo: () => any
  muteVideo: () => any
  muted: boolean
  index: number
}

class LockupVideoPreview extends React.Component<ILockupVideoPreview, IState> {
  private rootRef: React.RefObject<any>
  private playerRef: React.RefObject<HTMLInputElement>
  private videoResetTimeout: any
  private videoPlayTimeout: any
  private hideCursorTimeout: any
  private isFullscreen: boolean
  private wasFullscreen: boolean
  private videoHasAudio: boolean | undefined
  private showPreloader: boolean | undefined
  private videoHasLoaded: boolean

  constructor(props: ILockupVideoPreview) {
    super(props)

    this.state = {
      playState: PAUSE,
      wasTouchMoveDetected: false,
      hideCursor: false,
      videoPlayPromise: null,
      videoLooped: false,
      reportedEvents: [],
      videoProgress: 0,
      dragging: false
    }

    this.rootRef = React.createRef()
    this.playerRef = React.createRef()
    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)
    this.startVideo = this.startVideo.bind(this)
    this.stopVideo = this.stopVideo.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onPlayError = this.onPlayError.bind(this)
    this.pauseVideo = this.pauseVideo.bind(this)
    this.handleSeek = this.handleSeek.bind(this)
    this.onProgressDragStart = this.onProgressDragStart.bind(this)
    this.onProgressDragEnd = this.onProgressDragEnd.bind(this)
    this.onProgressDrag = this.onProgressDrag.bind(this)
    this.onProgressMouseOut = this.onProgressMouseOut.bind(this)

    this.onVideoProgress = this.onVideoProgress.bind(this)
    this.reportProgressEvent = this.reportProgressEvent.bind(this)
    this.getProgressFromTarget = this.getProgressFromTarget.bind(this)
    this.pauseIfVideoOffscreen = this.pauseIfVideoOffscreen.bind(this)
  }

  public componentWillUnmount() {
    this.discardVideoReset()
  }

  public render() {
    const {
      item,
      onItemLinkClick,
      muted,
      muteVideo,
      isCardHovered,
      index
    } = this.props

    const { playState } = this.state

    let videoLinkStyle
    let overlayStyle

    if (this.state.hideCursor) {
      videoLinkStyle = { cursor: 'none' }
      overlayStyle = { opacity: 0 }
    }

    const videoUrl = get(item, 'previews.icon_with_video_preview.video_url')
    const posterUrl = this.getPosterUrl()

    const rootStyles = classnames(styles.root, {
      [styles.isFullscreen]: this.isFullscreen,
      [styles.isCardHovered]: isCardHovered
    })

    this.isFullscreen =
      screenfull &&
      screenfull.isFullscreen &&
      screenfull.element === this.rootRef.current

    return (
      <div className={rootStyles} ref={this.rootRef}>
        <div
          role="button"
          data-test-selector="video-preview"
          className={styles.videoWrapper}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseOut}
          onMouseMove={this.onMouseMove}
          ref={this.playerRef}
        >
          <a
            className={classnames(styles.videoLink, 'video-link')}
            href={item.url}
            onClick={onItemLinkClick}
            style={videoLinkStyle}
            onTouchStart={this.onTouchStart}
            onTouchMove={this.onTouchMove}
            onTouchEnd={this.onTouchEnd}
            aria-label={item.name}
            role="button"
          >
            <EnhancedLazyLoad
              height="100%"
              once
              offset={200}
              disabled={index <= firstVisibleVideosCount}
            >
              <BaseVideoPlayer
                playState={playState}
                muteVideo={muteVideo}
                onVideoProgress={this.onVideoProgress}
                muted={muted}
                preload="none"
                className={styles.video}
                loop
                playsInline
                seek={this.state.seek}
                src={videoUrl}
                poster={posterUrl}
                onPlayError={this.onPlayError}
              />
            </EnhancedLazyLoad>
          </a>

          {this.isFullscreen && (
            <ClickOverlay
              hideCursor={this.state.hideCursor}
              playState={playState}
              onPlayVideo={this.startVideo}
              onPauseVideo={this.pauseVideo}
            />
          )}

          <div
            role="progressbar"
            data-test-selector="progress-bar"
            aria-valuenow={this.state.videoProgress}
            aria-valuemin={0}
            aria-valuemax={100}
            className={styles.progressBarWrapper}
            onClick={this.handleSeek}
            onMouseDown={this.onProgressDragStart}
            onMouseUp={this.onProgressDragEnd}
            onMouseMove={this.onProgressDrag}
            onMouseLeave={this.onProgressMouseOut}
          >
            <div
              className={styles.progressBar}
              style={{
                width: this.state.videoProgress + '%'
              }}
            />
          </div>

          <section
            data-test-selector="video-controls"
            className={styles.hoverOverlay}
            style={overlayStyle}
          >
            {this.renderVideoControls()}
          </section>
        </div>

        {this.showPreloader && (
          <div className={styles.loadingSpinner}>
            <LoadingSpinner />
          </div>
        )}
      </div>
    )
  }

  private getPosterUrl = () => {
    const { item } = this.props
    return (
      get(item, 'previews.icon_with_video_preview.image_urls[0].url') ||
      get(item, 'previews.icon_with_video_preview.landscape_url')
    )
  }

  private renderVideoControls = () => {
    const { muted, muteVideo, unmuteVideo } = this.props

    if (this.isFullscreen) {
      return (
        <section className={styles.fullscreenControls}>
          <VideoControlsFullscreen
            playState={this.state.playState}
            onPlay={this.startVideo}
            onPause={this.pauseVideo}
            onExitFullscreen={this.handleFullScreen}
            includeMuteUnmute={this.videoHasAudio}
            muted={muted}
            onMuteVideo={muteVideo}
            onUnmuteVideo={unmuteVideo}
            onMouseMove={this.onMouseMove}
          />
        </section>
      )
    }

    return (
      <section className={styles.hoverOverlayActions}>
        <LockupPreviewActionPanel
          {...this.props}
          includeFullscreen
          includeMuteUnmute={this.videoHasAudio}
          onMuteVideo={muteVideo}
          onUnmuteVideo={unmuteVideo}
          onFullscreen={this.handleFullScreen}
          muted={muted}
        />
      </section>
    )
  }

  private checkVideoHasAudio = () => {
    const video = this.getVideoElement()

    const listener = () => {
      this.videoHasAudio =
        video.mozHasAudio ||
        !!video.webkitAudioDecodedByteCount ||
        (!!video.audioTracks && video.audioTracks.length)

      video.removeEventListener('loadeddata', listener)
    }

    video.addEventListener('loadeddata', listener)
  }

  private handleFullScreen = () => {
    const { reportVideoFullscreen, item } = this.props

    if (screenfull && screenfull.enabled) {
      if (!screenfull.isFullscreen) {
        screenfull.on('change', this.handleFullScreenChange)

        if (reportVideoFullscreen) {
          reportVideoFullscreen(item.id)
        }
      }
      screenfull.toggle(this.rootRef.current)
    } else {
      const videoEl = this.getVideoElement()

      if (videoEl.webkitSupportsFullscreen) {
        this.startVideo()
        videoEl.webkitEnterFullscreen()
      }
    }
  }

  private handleFullScreenChange = () => {
    if (screenfull) {
      if (screenfull.isFullscreen) {
        this.startVideo()
        this.discardVideoReset()
        this.wasFullscreen = true
      } else {
        screenfull.off('change', this.handleFullScreenChange)
        this.stopVideo()
      }
    }
  }

  private onVideoProgress(
    playProgress: number,
    currentTime: number,
    duration: number
  ) {
    this.setState({
      duration,
      videoProgress: playProgress
    })

    this.reportProgressEvent(playProgress)
    this.pauseIfVideoOffscreen()
  }

  private reportProgressEvent(playProgress: number) {
    if (playProgress === 0) {
      this.reportEvent('video_start')
    }

    if (playProgress >= 25 && playProgress < 50) {
      this.reportEvent('25_percent')
    }

    if (playProgress >= 50 && playProgress < 75) {
      this.reportEvent('50_percent')
    }

    if (playProgress >= 75) {
      this.reportEvent('75_percent')
    }

    if (playProgress + 1 >= 100) {
      this.reportEvent('video_complete')
      this.setState({
        videoLooped: true
      })
    }
  }

  private getProgressFromTarget(event: any) {
    if (this.playerRef.current) {
      const progressbar = event.target.getBoundingClientRect()
      const player = this.playerRef.current.getBoundingClientRect()
      const delta = event.clientX - progressbar.left
      const progress = Math.round((delta * 100) / player.width)
      return progress
    } else {
      return null
    }
  }

  private handleSeek(event: any) {
    const progress = this.getProgressFromTarget(event)
    if (!this.state.dragging && this.state.duration && progress) {
      this.setState({
        videoProgress: progress,
        seek: this.state.duration * (progress / 100)
      })
    }
  }

  private onProgressDragStart(event: React.MouseEvent | React.TouchEvent) {
    const progress = this.getProgressFromTarget(event)
    if (this.state.duration && progress) {
      this.setState({
        dragging: true,
        videoProgress: progress,
        seek: this.state.duration * (progress / 100)
      })
    }
  }

  private onProgressDragEnd(event: React.MouseEvent | React.TouchEvent) {
    if (event.cancelable) {
      event.preventDefault()
    }
    this.setState({ dragging: false })
    if (this.state.duration) {
      this.startVideo()
    }
  }

  private onProgressDrag(event: React.MouseEvent | React.TouchEvent) {
    const progress = this.getProgressFromTarget(event)
    const { dragging } = this.state

    if (dragging && this.state.duration && progress) {
      this.setState({
        videoProgress: progress,
        seek: this.state.duration * (progress / 100)
      })
    }
  }

  private onProgressMouseOut(event: React.MouseEvent | React.TouchEvent) {
    if (this.state.dragging) {
      this.onProgressDragEnd(event)
    }
  }

  private pauseIfVideoOffscreen() {
    if (this.state.playState === PLAY) {
      if (this.playerRef.current) {
        const pos = this.playerRef.current.getBoundingClientRect()

        if (document.documentElement) {
          const out: any = {
            top:
              pos.top >
              (window.innerHeight || document.documentElement.clientHeight),
            bottom: pos.bottom < 0
          }

          if (out.top || out.bottom) {
            this.stopVideo()
          }
        }
      }
    }
  }

  private hasEventReported(eventName: any) {
    return this.state.reportedEvents.includes(eventName)
  }

  private reportEvent(eventName: string) {
    if (!this.state.videoLooped && !this.hasEventReported(eventName)) {
      const { item } = this.props
      const videoSrc = get(item, 'previews.icon_with_video_preview.video_url')

      if (this.props.reportVideoProgress) {
        this.props.reportVideoProgress(eventName, videoSrc)
        this.setState({
          reportedEvents: [eventName, ...this.state.reportedEvents]
        })
      }
    }
  }

  private scheduleVideoReset() {
    this.videoResetTimeout = setTimeout(() => {
      this.setState({ playState: RESET, seek: undefined })

      // This fixes a rendering issue with chrome when exiting fullscreen.
      // Without this, when the video resets the video will dissapear and
      // you'll only see the black background of this `video-link`
      if (this.wasFullscreen) {
        const el = this.rootRef.current.getElementsByClassName('video-link')[0]
        this.wasFullscreen = false

        if (el) {
          el.style.display = 'flex'
          window.setTimeout(() => (el.style.display = ''))
        }
      }
    }, 5000)
  }

  private discardVideoReset() {
    if (this.videoResetTimeout) {
      clearTimeout(this.videoResetTimeout)
      this.videoResetTimeout = null
    }
  }

  private pauseVideo() {
    this.setState({ playState: PAUSE, hideCursor: false })
    clearTimeout(this.hideCursorTimeout)
  }

  private toggleVideo() {
    if (this.state.playState !== PLAY) {
      this.startVideo()
    } else {
      this.stopVideo()
    }
  }

  private scheduleVideoPlay() {
    this.discardScheduledVideoPlay()
    this.videoPlayTimeout = setTimeout(() => {
      this.setState({ playState: PLAY })
    }, 20)
  }

  private discardScheduledVideoPlay() {
    if (this.videoPlayTimeout) {
      clearTimeout(this.videoPlayTimeout)
      this.videoPlayTimeout = null
    }
  }

  private onTouchStart() {
    this.setState({
      wasTouchMoveDetected: false
    })
  }

  private onTouchMove() {
    this.setState({
      wasTouchMoveDetected: true
    })
  }

  private onTouchEnd(event: React.TouchEvent<HTMLAnchorElement>) {
    if (event.cancelable) {
      event.preventDefault()
    }

    if (!this.state.wasTouchMoveDetected) {
      this.toggleVideo()
      this.showLoadingSpinner()
    }
  }

  private showLoadingSpinner() {
    if (!this.videoHasLoaded) {
      const video = this.getVideoElement()

      const listener = () => {
        this.showPreloader = false
        this.videoHasLoaded = true
        video.removeEventListener('loadeddata', listener)
      }

      if (video) {
        this.showPreloader = true
        video.addEventListener('loadeddata', listener)
      }
    }
  }

  private getVideoElement() {
    return (
      this.rootRef.current &&
      this.rootRef.current.getElementsByTagName('video')[0]
    )
  }

  private isVideoLoaded() {
    return !!this.getVideoElement()
  }

  private startVideo() {
    this.hideCursorTimeout = setTimeout(() => {
      this.setState({ hideCursor: true })
    }, 3000)

    this.discardVideoReset()
    this.scheduleVideoPlay()
  }

  private stopVideo() {
    if (this.hideCursorTimeout) {
      clearTimeout(this.hideCursorTimeout)
    }

    this.setState({ hideCursor: false })

    this.discardScheduledVideoPlay()
    this.pauseVideo()
    this.scheduleVideoReset()
  }

  private onMouseMove() {
    if (this.state.hideCursor) {
      this.setState({ hideCursor: false })
    }

    clearTimeout(this.hideCursorTimeout)

    if (this.state.playState === PLAY) {
      this.hideCursorTimeout = setTimeout(() => {
        this.setState({ hideCursor: true })
      }, 3000)
    }
  }

  private onMouseOut = () => {
    if (!this.isFullscreen) {
      this.setState({ dragging: false })
      this.stopVideo()
    }
  }

  private onMouseEnter = () => {
    if (this.isVideoLoaded()) {
      if (this.videoHasAudio === undefined) {
        this.checkVideoHasAudio()
      }

      this.showLoadingSpinner()

      if (!this.isFullscreen) {
        this.startVideo()
      }
    } else {
      setTimeout(this.onMouseEnter, 50)
    }
  }

  private onPlayError(error: any) {
    if (
      error.name === 'NotAllowedError' &&
      this.props.setUserVideoAutoPlayStatus
    ) {
      this.props.setUserVideoAutoPlayStatus(false)
    } else {
      throw error
    }
  }
}

export default LockupVideoPreview
